// 📄 pages/APIs/AribaApiTester.tsx
// Explorador de APIs de Ariba. Dos orígenes de datos posibles:
//  - Con clienteId (entraste desde un proyecto): los endpoints son los
//    reales configurados para ESE CLIENTE en Configuración > APIs, y
//    "Enviar Petición" ejecuta una llamada REAL a Ariba vía la Cloud
//    Function ariba_ExecuteRequest (ver functions/callable/) — nunca
//    desde el navegador, las credenciales nunca salen del backend.
//  - Sin clienteId (entraste desde el Home, sin proyecto): no hay cliente
//    al que atarlos, así que el usuario arma y guarda sus propias APIs
//    "personales" (ligadas a su cuenta, ver obtenerMisApis/guardarMisApis
//    en clienteConexionService.ts) directamente en esta misma pantalla —
//    reutiliza ApiDefinitionCard.tsx, el mismo editor que usa Configuración.
//    Este modo sigue simulado: la callable todavía no soporta ejecutar
//    sin un cliente detrás (ver aribaExecuteRequest.js), queda para una
//    segunda iteración.
// En ambos casos, params/body/cabeceras del endpoint elegido son editables
// para la prueba puntual, con su propio botón de guardado.
import React, { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import Breadcrumbs from '../../components/ui/Breadcrumbs.tsx';
import LimiteUsoAlerta from '../../components/ui/LimiteUsoAlerta.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { functionsClient } from '../../firebase.js';
import { ApiConnectionCredentials, EMPTY_API_CREDENTIALS } from './apiCredentials.types.ts';
import {
  obtenerConfigCliente, guardarConfigCliente, obtenerMisApis, guardarMisApis,
  ApisCredenciales, ApiDefinition, ApiEndpointConfig, ApiEndpointParam
} from '../../services/clienteConexionService.ts';
import { getApiTemplates, ApiTemplate } from '../../services/catalogoApisService.ts';
import { construirUrlPreview } from '../../components/Configuracion/EndpointConfigCard.tsx';
import ApiDefinitionCard from '../../components/Configuracion/ApiDefinitionCard.tsx';
import { extraerNombresPathVariables, obtenerPathVariablesFaltantes } from '../../utils/pathVariables.ts';
import {
  Link2, Settings, Play, Copy, Check, Info, FileCode, KeyRound, Pencil, RefreshCw,
  PackageX, Save, Plus, X, User, AlertTriangle, XCircle
} from 'lucide-react';

interface AribaApiTesterProps {
  setPage: (page: string) => void;
  embedded?: boolean;
  clienteId?: string;
  projectId?: string;
  credentials?: ApiConnectionCredentials;
}

// Interruptor manual para volver a la respuesta simulada sin necesitar un
// redeploy (p. ej. si algo se rompe en producción) — en cliente siempre
// pasa por acá primero, nunca directo a la llamada real.
const MODO_SIMULACION = false;

interface ResultadoEjecucion {
  ok: boolean;
  status: number;
  statusText: string;
  durationMs: number;
  sizeBytes: number;
  headers: Record<string, string>;
  body: unknown;
  requestUrl: string | null;
  error: { code: string; message: string; hint: string | null } | null;
}

const METODO_COLOR: Record<string, string> = {
  GET: 'bg-emerald-500/20 text-emerald-400',
  POST: 'bg-blue-500/20 text-blue-400',
  PUT: 'bg-amber-500/20 text-amber-400',
  PATCH: 'bg-purple-500/20 text-purple-400',
  DELETE: 'bg-red-500/20 text-red-400'
};

const METODOS_CON_BODY = ['POST', 'PUT', 'PATCH'];

const tabClass = (active: boolean) =>
  `pb-2.5 px-4 relative ${active ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-500 hover:text-white'}`;

const inputClass = "w-full bg-black/40 border border-white/10 rounded-lg p-2.5 text-xs font-mono text-white outline-none focus:border-blue-500";

const generarId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const nuevaApiPersonal = (): ApiDefinition => ({
  id: generarId(),
  tipo: 'ariba',
  authTipo: 'oauth',
  nombre: '',
  protocolo: '',
  ambiente: '',
  urlBase: '',
  api: '',
  apiKey: '',
  oauthSecret: '',
  clientId: '',
  dataCenter: '',
  realm: '',
  anid: '',
  tokenUrl: '',
  grantType: '',
  usuario: '',
  contraseña: '',
  tieneCredencialesGuardadas: false,
  endpoints: []
});

const formatBytes = (n: number): string => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  return `${(n / 1024).toFixed(1)} KB`;
};

const statusBadgeClass = (resultado: ResultadoEjecucion): string => {
  if (!resultado.status) return 'bg-gray-500/20 text-gray-400';
  if (resultado.status >= 200 && resultado.status < 300) return 'bg-emerald-500/20 text-emerald-400';
  if (resultado.status >= 500) return 'bg-red-500/20 text-red-400';
  if (resultado.status >= 400) return 'bg-amber-500/20 text-amber-400';
  return 'bg-gray-500/20 text-gray-400';
};

export default function AribaApiTester({ setPage, embedded = false, clienteId, projectId, credentials = EMPTY_API_CREDENTIALS }: AribaApiTesterProps) {
  const esPersonal = !clienteId;
  const { profile } = useAuth();
  // Cuota mensual compartida entre este tester, Buscar Proveedores y
  // Buscar Facturas (ver functions/services/monthlyQuota.js) — el
  // contador guardado puede ser de un mes anterior (reset perezoso), así
  // que si no coincide con el mes actual se muestra 0, igual que hace el
  // callable antes de decidir si bloquea.
  const mesActual = `${new Date().getUTCFullYear()}-${String(new Date().getUTCMonth() + 1).padStart(2, '0')}`;
  const usoApiTesterVigente = profile?.usoApiTesterMes === mesActual ? (profile?.usoApiTesterCantidad ?? 0) : 0;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Lista COMPLETA de APIs del cliente (todos los tipos) — necesaria para
  // guardar sin pisar APIs de otros tipos (p. ej. S/4HANA) que no se
  // muestran acá. Solo aplica en modo cliente.
  const [todasLasApis, setTodasLasApis] = useState<ApiDefinition[]>([]);
  // Modo personal: el usuario administra directamente su propia lista acá
  // mismo (no hay una "Configuración" separada para APIs personales).
  const [misApis, setMisApis] = useState<ApiDefinition[]>([]);
  const [misApisExpandidas, setMisApisExpandidas] = useState<Record<string, boolean>>({});
  const [misApisEditables, setMisApisEditables] = useState<Record<string, boolean>>({});
  const [guardandoMisApis, setGuardandoMisApis] = useState(false);
  const [errorMisApis, setErrorMisApis] = useState<string | null>(null);
  const [okMisApis, setOkMisApis] = useState(false);

  const [apiSeleccionadaId, setApiSeleccionadaId] = useState('');
  const [selectedEndpointId, setSelectedEndpointId] = useState('');
  const [payload, setPayload] = useState('');
  const [paramsEdit, setParamsEdit] = useState<ApiEndpointParam[]>([]);
  const [headersEdit, setHeadersEdit] = useState<ApiEndpointParam[]>([]);
  const [pathVariablesEdit, setPathVariablesEdit] = useState<ApiEndpointParam[]>([]);
  const [resultado, setResultado] = useState<ResultadoEjecucion | null>(null);
  const [outputTab, setOutputTab] = useState<'body' | 'headers'>('body');
  const [enviando, setEnviando] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'body' | 'params' | 'headers' | 'pathVariables'>('params');
  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState<string | null>(null);
  const [mostrarConfirmDelete, setMostrarConfirmDelete] = useState(false);
  const canceladoRef = useRef(false);
  // Catálogo de APIs Estándar (Admin) — mismo uso que en
  // ConfiguracionApis.tsx: sugerencia de URL Base/URL del Token por Data
  // Center y prellenado rápido por plantilla dentro de ApiDefinitionCard.
  const [catalogo, setCatalogo] = useState<ApiTemplate[]>([]);

  useEffect(() => {
    getApiTemplates()
      .then(setCatalogo)
      .catch((err) => console.error('Error cargando el catálogo de APIs estándar:', err));
  }, []);

  const cargarEdicion = (ep?: ApiEndpointConfig) => {
    setPayload(ep?.bodyEjemplo || '');
    setParamsEdit(ep?.params ? ep.params.map((p) => ({ ...p })) : []);
    setHeadersEdit(ep?.headers ? ep.headers.map((h) => ({ ...h })) : []);
    setPathVariablesEdit(ep?.pathVariables ? ep.pathVariables.map((v) => ({ ...v })) : []);
    setResultado(null);
    setOutputTab('body');
    setActiveTab('params');
    setGuardadoOk(false);
    setErrorGuardar(null);
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    const fetchPromise = clienteId
      ? obtenerConfigCliente<ApisCredenciales>(clienteId, 'apis')
      : obtenerMisApis();

    fetchPromise
      .then((config) => {
        const lista = config.apis || [];
        if (clienteId) setTodasLasApis(lista);
        else setMisApis(lista.filter((api) => (api.tipo || 'ariba') === 'ariba'));

        const primera = lista.find((api) => (api.tipo || 'ariba') === 'ariba');
        const primerEndpoint = primera?.endpoints[0];
        setApiSeleccionadaId(primera?.id || '');
        setSelectedEndpointId(primerEndpoint?.id || '');
        cargarEdicion(primerEndpoint);
      })
      .catch((err) => {
        console.error('Error cargando APIs de Ariba:', err);
        setError('No se pudo cargar la configuración guardada.');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const apisAriba = clienteId
    ? todasLasApis.filter((api) => (api.tipo || 'ariba') === 'ariba')
    : misApis;
  const apiSeleccionada = apisAriba.find((a) => a.id === apiSeleccionadaId);
  const endpoints = apiSeleccionada?.endpoints || [];
  const selectedEndpointGuardado = endpoints.find((e) => e.id === selectedEndpointId);
  // El endpoint "editado" (lo que se ve/envía/guarda) combina el guardado
  // con los cambios locales de params/body/headers/pathVariables todavía
  // no persistidos.
  const selectedEndpoint: ApiEndpointConfig | undefined = selectedEndpointGuardado
    ? { ...selectedEndpointGuardado, bodyEjemplo: payload, params: paramsEdit, headers: headersEdit, pathVariables: pathVariablesEdit }
    : undefined;

  // El path en sí (endpoint.endpoint) no es editable desde este panel —
  // solo se edita vía EndpointConfigCard (ver ApiDefinitionCard.tsx) — así
  // que basta con mirar el path guardado para decidir si el tab "Path
  // Variables" debe existir.
  const nombresPathVariables = selectedEndpoint ? extraerNombresPathVariables(selectedEndpoint.endpoint) : [];
  const tienePathVariables = nombresPathVariables.length > 0;
  const pathVariablesFaltantes = selectedEndpoint
    ? obtenerPathVariablesFaltantes(selectedEndpoint.endpoint, pathVariablesEdit)
    : [];

  const handleSeleccionarApi = (id: string) => {
    setApiSeleccionadaId(id);
    const api = apisAriba.find((a) => a.id === id);
    const primerEndpoint = api?.endpoints[0];
    setSelectedEndpointId(primerEndpoint?.id || '');
    cargarEdicion(primerEndpoint);
  };

  const handleEndpointSelect = (ep: ApiEndpointConfig) => {
    setSelectedEndpointId(ep.id);
    cargarEdicion(ep);
  };

  const handleCopyResponse = () => {
    if (!resultado) return;
    const texto = outputTab === 'body'
      ? (typeof resultado.body === 'string' ? resultado.body : JSON.stringify(resultado.body, null, 2))
      : JSON.stringify(resultado.headers, null, 2);
    navigator.clipboard.writeText(texto);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const urlActual = apiSeleccionada && selectedEndpoint
    ? construirUrlPreview(apiSeleccionada.urlBase, apiSeleccionada.api, selectedEndpoint)
    : '';

  const ejecutarPeticion = async () => {
    if (!selectedEndpoint || !apiSeleccionada) return;

    canceladoRef.current = false;
    setEnviando(true);
    setResultado(null);
    setOutputTab('body');

    // Modo personal: la ejecución real todavía no está soportada por el
    // backend (ver aribaExecuteRequest.js) — se simula siempre, sin
    // depender del interruptor MODO_SIMULACION.
    if (esPersonal || MODO_SIMULACION) {
      setTimeout(() => {
        if (canceladoRef.current) return;
        setResultado({
          ok: true,
          status: 200,
          statusText: 'OK (simulado)',
          durationMs: 180,
          sizeBytes: 0,
          headers: Object.fromEntries(headersEdit.filter((h) => h.clave).map((h) => [h.clave, h.valor])),
          body: {
            simulado: true,
            metodo: selectedEndpoint.metodo,
            url: urlActual,
            ...(METODOS_CON_BODY.includes(selectedEndpoint.metodo) ? { bodyEnviado: payload } : {}),
            mensaje: esPersonal
              ? 'Respuesta simulada — la ejecución real de APIs personales todavía no está disponible.'
              : 'Respuesta simulada (modo simulación activo).'
          },
          requestUrl: urlActual,
          error: null
        });
        setEnviando(false);
      }, 900);
      return;
    }

    try {
      const ejecutar = httpsCallable(functionsClient, 'ariba_ExecuteRequest');
      const resp = await ejecutar({
        clienteId,
        projectId,
        apiId: apiSeleccionada.id,
        endpointId: selectedEndpoint.id,
        metodo: selectedEndpoint.metodo,
        params: paramsEdit,
        headers: headersEdit,
        pathVariables: pathVariablesEdit,
        body: payload
      });
      if (canceladoRef.current) return;
      setResultado(resp.data as ResultadoEjecucion);
    } catch (err: any) {
      if (canceladoRef.current) return;
      setResultado({
        ok: false,
        status: 0,
        statusText: err.code || 'error',
        durationMs: 0,
        sizeBytes: 0,
        headers: {},
        body: null,
        requestUrl: null,
        error: { code: err.code || 'client_error', message: err.message || 'Error ejecutando la petición.', hint: null }
      });
    } finally {
      if (!canceladoRef.current) setEnviando(false);
    }
  };

  const handleSendRequest = () => {
    if (!selectedEndpoint) return;
    if (selectedEndpoint.metodo === 'DELETE') {
      setMostrarConfirmDelete(true);
      return;
    }
    ejecutarPeticion();
  };

  const handleConfirmarDelete = () => {
    setMostrarConfirmDelete(false);
    ejecutarPeticion();
  };

  const handleCancelarEnvio = () => {
    canceladoRef.current = true;
    setEnviando(false);
  };

  const handleGuardarCambios = async () => {
    if (!apiSeleccionada || !selectedEndpointGuardado) return;
    setGuardando(true);
    setErrorGuardar(null);
    setGuardadoOk(false);
    try {
      const endpointActualizado: ApiEndpointConfig = {
        ...selectedEndpointGuardado,
        bodyEjemplo: payload,
        params: paramsEdit,
        headers: headersEdit,
        pathVariables: pathVariablesEdit
      };
      const patch = (api: ApiDefinition) =>
        api.id === apiSeleccionada.id
          ? { ...api, endpoints: api.endpoints.map((ep) => (ep.id === endpointActualizado.id ? endpointActualizado : ep)) }
          : api;

      if (clienteId) {
        // Se actualiza el endpoint dentro de la lista COMPLETA (todos los
        // tipos de API) — si se guardara solo apisAriba se perderían las
        // APIs de otros tipos (p. ej. S/4HANA) al reemplazar el array
        // completo en Firestore.
        const actualizadas = todasLasApis.map(patch);
        await guardarConfigCliente(clienteId, 'apis', { apis: actualizadas });
        setTodasLasApis(actualizadas);
      } else {
        const actualizadas = misApis.map(patch);
        await guardarMisApis(actualizadas);
        setMisApis(actualizadas);
      }
      setGuardadoOk(true);
      setTimeout(() => setGuardadoOk(false), 3000);
    } catch (err: any) {
      setErrorGuardar(err.message || 'Error al guardar los cambios.');
    } finally {
      setGuardando(false);
    }
  };

  const addParam = () => setParamsEdit((prev) => [...prev, { clave: '', valor: '' }]);
  const updateParam = (i: number, field: 'clave' | 'valor', value: string) =>
    setParamsEdit((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));
  const removeParam = (i: number) => setParamsEdit((prev) => prev.filter((_, idx) => idx !== i));

  const addHeader = () => setHeadersEdit((prev) => [...prev, { clave: '', valor: '' }]);
  const updateHeader = (i: number, field: 'clave' | 'valor', value: string) =>
    setHeadersEdit((prev) => prev.map((h, idx) => (idx === i ? { ...h, [field]: value } : h)));
  const removeHeader = (i: number) => setHeadersEdit((prev) => prev.filter((_, idx) => idx !== i));

  // A diferencia de Params/Headers (listas libres), los nombres de Path
  // Variables los define el path guardado (":nombre", ver
  // nombresPathVariables) — acá solo se completa el valor de cada uno,
  // no se agregan ni se quitan filas.
  const valorPathVariable = (clave: string): string => pathVariablesEdit.find((v) => v.clave === clave)?.valor || '';
  const updatePathVariableValor = (clave: string, valor: string) =>
    setPathVariablesEdit((prev) => {
      const existe = prev.some((v) => v.clave === clave);
      return existe ? prev.map((v) => (v.clave === clave ? { ...v, valor } : v)) : [...prev, { clave, valor }];
    });

  // ── Gestión de APIs personales (solo modo sin clienteId) ──────────────
  const handleAgregarApiPersonal = () => {
    const api = nuevaApiPersonal();
    setMisApis((prev) => [...prev, api]);
    setMisApisExpandidas((prev) => ({ ...prev, [api.id]: true }));
    setMisApisEditables((prev) => ({ ...prev, [api.id]: true }));
  };

  const handleActualizarApiPersonal = (id: string, actualizada: ApiDefinition) => {
    setMisApis((prev) => prev.map((api) => (api.id === id ? actualizada : api)));
  };

  const handleEliminarApiPersonal = (id: string) => {
    setMisApis((prev) => prev.filter((api) => api.id !== id));
    if (apiSeleccionadaId === id) {
      setApiSeleccionadaId('');
      setSelectedEndpointId('');
      cargarEdicion(undefined);
    }
  };

  const toggleMisApiExpandida = (id: string) => setMisApisExpandidas((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleMisApiEditable = (id: string) => {
    setMisApisEditables((prev) => ({ ...prev, [id]: !prev[id] }));
    setMisApisExpandidas((prev) => ({ ...prev, [id]: true }));
  };

  const handleGuardarMisApis = async () => {
    setGuardandoMisApis(true);
    setErrorMisApis(null);
    setOkMisApis(false);
    try {
      await guardarMisApis(misApis);
      setMisApis((prev) =>
        prev.map((api) => ({
          ...api,
          tieneCredencialesGuardadas: api.tieneCredencialesGuardadas || Boolean((api.apiKey && api.oauthSecret) || api.contraseña),
          apiKey: '',
          oauthSecret: '',
          contraseña: ''
        }))
      );
      setMisApisExpandidas({});
      setMisApisEditables({});
      setOkMisApis(true);
      setTimeout(() => setOkMisApis(false), 3000);
    } catch (err: any) {
      setErrorMisApis(err.message || 'Error al guardar tus APIs.');
    } finally {
      setGuardandoMisApis(false);
    }
  };

  const bodyTexto = resultado
    ? (typeof resultado.body === 'string' ? resultado.body : JSON.stringify(resultado.body, null, 2))
    : '';
  const headersTexto = resultado ? JSON.stringify(resultado.headers, null, 2) : '';

  // construirUrlPreview ya reemplazó las Path Variables que sí tienen
  // valor — cualquier ":nombre" que quede en la URL es, por construcción,
  // una que falta. Se resalta en rojo en vez de mostrar la URL como si
  // fuera válida para enviar.
  const renderUrlConPathVariables = (url: string) =>
    url.split(/(:[A-Za-z_][A-Za-z0-9_]*)/g).map((parte, i) =>
      /^:[A-Za-z_][A-Za-z0-9_]*$/.test(parte)
        ? <span key={i} className="text-red-400 font-semibold">{parte}</span>
        : <React.Fragment key={i}>{parte}</React.Fragment>
    );

  return (
    <div className={embedded ? 'text-white' : 'min-h-screen p-4 md:p-8 text-white bg-transparent relative overflow-hidden'}>
      {!embedded && (
        <>
          <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-blue-900/20 rounded-full blur-[120px] pointer-events-none z-0" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-cyan-900/10 rounded-full blur-[100px] pointer-events-none z-0" />
        </>
      )}

      {mostrarConfirmDelete && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full space-y-4">
            <h3 className="text-white font-bold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" /> Confirmar DELETE
            </h3>
            <p className="text-sm text-gray-400">
              Vas a ejecutar un <strong className="text-red-400">DELETE real</strong> contra Ariba:
            </p>
            <p className="font-mono text-[11px] text-gray-300 bg-black/30 border border-white/10 rounded-lg p-2.5 break-all">{renderUrlConPathVariables(urlActual)}</p>
            <p className="text-xs text-gray-500">Esta acción no se puede deshacer. ¿Continuar?</p>
            <div className="flex gap-3 justify-end pt-2">
              <button
                onClick={() => setMostrarConfirmDelete(false)}
                className="px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-white/10 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarDelete}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-500 text-white transition-colors"
              >
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={embedded ? 'flex flex-col h-full' : 'relative z-10 max-w-7xl mx-auto flex flex-col h-full'}>
        {!embedded && <Breadcrumbs currentPage="ariba-api-test" setPage={setPage} />}

        <header className="mb-8 mt-6">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <span className="p-2 bg-blue-500/20 rounded-xl text-blue-400">
              <Link2 className="w-8 h-8" />
            </span>
            SAP Ariba API Explorer
          </h1>
          <p className="text-gray-400 text-sm font-light mt-1 max-w-2xl">
            {esPersonal
              ? 'Arma y prueba tus propias APIs de Ariba, guardadas en tu cuenta (no en un proyecto ni cliente).'
              : 'Prueba los endpoints de Ariba configurados para este cliente (Supplier Data, Contract Workspaces, etc.), armados a partir de la Configuración del proyecto.'}
          </p>
        </header>

        {!esPersonal && (
          <div className="mb-8 flex flex-wrap items-center gap-x-6 gap-y-2 bg-white/5 border border-white/10 rounded-2xl px-5 py-3.5 text-xs">
            <span className={`flex items-center gap-1.5 font-bold uppercase tracking-wider ${credentials.clientId ? 'text-emerald-400' : 'text-amber-400'}`}>
              <KeyRound className="w-3.5 h-3.5" /> {credentials.clientId ? 'Credenciales activas' : 'Sin credenciales configuradas'}
            </span>
            {credentials.clientId && (
              <>
                <span className="text-gray-500">Client ID: <span className="text-white font-mono">{credentials.clientId}</span></span>
                <span className="text-gray-500">Data Center: <span className="text-white font-mono">{credentials.dataCenter || '-'}</span></span>
              </>
            )}
            <button
              onClick={() => setPage('pruebas-apis')}
              className="ml-auto flex items-center gap-1.5 text-blue-400 hover:text-blue-300 font-semibold transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" /> Editar credenciales
            </button>
          </div>
        )}

        {esPersonal && (
          <div className="mb-8 flex items-center gap-2 text-xs text-gray-400 bg-white/5 border border-white/10 rounded-2xl px-5 py-3.5">
            <User className="w-3.5 h-3.5 text-blue-400" />
            Estas APIs se guardan en tu cuenta — solo tú las ves, no quedan asociadas a ningún proyecto ni cliente. La ejecución real todavía no está disponible en este modo: las respuestas siguen siendo simuladas.
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-400 bg-white/5 border border-white/10 rounded-[2.5rem] p-12">
            <RefreshCw className="w-4 h-4 animate-spin" /> Cargando APIs de Ariba...
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-2xl text-sm">{error}</div>
        ) : (
          <>
            {esPersonal && (
              <section className="mb-8 bg-white/5 border border-white/10 rounded-[2.5rem] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-white">Mis APIs de Ariba</h3>
                  <button
                    type="button"
                    onClick={handleAgregarApiPersonal}
                    className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Agregar API
                  </button>
                </div>

                {errorMisApis && (
                  <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs mb-4">{errorMisApis}</div>
                )}
                {okMisApis && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-lg text-xs mb-4">Guardado en tu cuenta.</div>
                )}

                {misApis.length === 0 ? (
                  <p className="text-xs text-gray-500 bg-black/20 border border-white/10 rounded-2xl p-5">
                    Todavía no has creado ninguna API. Agrega una con sus credenciales y endpoints para empezar a probar.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {misApis.map((api) => (
                      <ApiDefinitionCard
                        key={api.id}
                        api={api}
                        expandida={!!misApisExpandidas[api.id]}
                        editable={!!misApisEditables[api.id]}
                        catalogo={catalogo}
                        onToggleExpandida={() => toggleMisApiExpandida(api.id)}
                        onToggleEditable={() => toggleMisApiEditable(api.id)}
                        onChange={(actualizada) => handleActualizarApiPersonal(api.id, actualizada)}
                        onRemove={() => handleEliminarApiPersonal(api.id)}
                      />
                    ))}
                  </div>
                )}

                {misApis.length > 0 && (
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={handleGuardarMisApis}
                      disabled={guardandoMisApis}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-5 py-2.5 rounded-xl text-sm font-semibold text-[#fff] transition-all"
                    >
                      {guardandoMisApis ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {guardandoMisApis ? 'Guardando...' : 'Guardar configuración'}
                    </button>
                  </div>
                )}
              </section>
            )}

            {apisAriba.length === 0 ? (
              !esPersonal && (
                <EmptyState
                  title="Todavía no hay APIs de Ariba configuradas"
                  message="Ve a Configuración del proyecto → APIs para agregar una API de Ariba con sus endpoints."
                />
              )
            ) : (
              <>
                {apisAriba.length > 1 && (
                  <div className="mb-6 max-w-sm">
                    <label className="block text-xs font-medium text-gray-400 mb-1">API</label>
                    <select
                      className="w-full bg-black/30 border border-white/10 rounded-lg p-2.5 text-sm text-white outline-none focus:border-blue-500"
                      value={apiSeleccionadaId}
                      onChange={(e) => handleSeleccionarApi(e.target.value)}
                    >
                      {apisAriba.map((api) => <option key={api.id} value={api.id}>{api.nombre || 'Sin nombre'}</option>)}
                    </select>
                  </div>
                )}

                {endpoints.length === 0 ? (
                  <EmptyState
                    title={`"${apiSeleccionada?.nombre || 'Esta API'}" todavía no tiene endpoints`}
                    message={esPersonal ? 'Ábrela arriba, toca "Editar" y agrega uno.' : 'Agrégalos desde Configuración del proyecto → APIs.'}
                  />
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
                    <aside className="lg:col-span-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[2rem] p-6 flex flex-col gap-2">
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 px-3 truncate">
                        Endpoints de {apiSeleccionada?.nombre || 'Ariba'}
                      </h3>
                      <div className="space-y-1.5 overflow-y-auto max-h-[500px]">
                        {endpoints.map((ep) => (
                          <button
                            key={ep.id}
                            onClick={() => handleEndpointSelect(ep)}
                            className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 flex flex-col gap-1 ${
                              selectedEndpointId === ep.id
                                ? 'bg-blue-600/90 text-white shadow-lg'
                                : 'text-gray-400 hover:bg-white/10 hover:text-white'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded ${METODO_COLOR[ep.metodo] || METODO_COLOR.GET}`}>
                                {ep.metodo}
                              </span>
                              <span className="font-semibold text-xs truncate">{ep.nombre || ep.endpoint || 'Sin nombre'}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </aside>

                    <section className="lg:col-span-5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[2.5rem] p-8 flex flex-col justify-between">
                      {selectedEndpoint && apiSeleccionada && (
                        <>
                          <div className="space-y-6">
                            <div>
                              <h3 className="text-xl font-bold flex items-center gap-2">
                                <Settings className="w-5 h-5 text-blue-400" /> Configurar Petición
                              </h3>
                              <p className="text-gray-400 text-xs mt-1 leading-relaxed">
                                {apiSeleccionada.protocolo || 'Protocolo sin especificar'} · {apiSeleccionada.ambiente || 'Ambiente sin especificar'}
                              </p>
                            </div>

                            <LimiteUsoAlerta
                              cantidad={usoApiTesterVigente}
                              limite={profile?.limiteApiTesterMensual}
                              etiqueta="ejecuciones de API Tester este mes"
                            />

                            <div className="bg-black/30 border border-white/10 rounded-xl p-3.5 flex items-center gap-2.5 font-mono text-xs overflow-hidden">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${METODO_COLOR[selectedEndpoint.metodo] || METODO_COLOR.GET}`}>
                                {selectedEndpoint.metodo}
                              </span>
                              <span className="text-gray-300 truncate w-full" title={urlActual}>{renderUrlConPathVariables(urlActual)}</span>
                            </div>

                            <div>
                              <div className="flex border-b border-white/10 mb-4 text-xs font-semibold">
                                <button onClick={() => setActiveTab('body')} className={tabClass(activeTab === 'body')}>Body</button>
                                <button onClick={() => setActiveTab('params')} className={tabClass(activeTab === 'params')}>Params</button>
                                <button onClick={() => setActiveTab('headers')} className={tabClass(activeTab === 'headers')}>Cabeceras</button>
                                {tienePathVariables && (
                                  <button onClick={() => setActiveTab('pathVariables')} className={tabClass(activeTab === 'pathVariables')}>
                                    Path Variables
                                    {pathVariablesFaltantes.length > 0 && (
                                      <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-red-400" title="Faltan valores" />
                                    )}
                                  </button>
                                )}
                              </div>

                              {activeTab === 'body' ? (
                                METODOS_CON_BODY.includes(selectedEndpoint.metodo) ? (
                                  <textarea
                                    className="w-full h-72 font-mono text-xs bg-black/40 border border-white/10 rounded-xl p-4 text-blue-200 outline-none focus:border-blue-500 resize-none"
                                    value={payload}
                                    onChange={(e) => setPayload(e.target.value)}
                                    placeholder='{ "campo": "valor" }'
                                  />
                                ) : (
                                  <div className="flex flex-col items-center justify-center text-center h-72 border border-white/5 rounded-xl bg-black/10 text-gray-500">
                                    <Info className="w-8 h-8 text-gray-600 mb-2" />
                                    <p className="text-xs">Los métodos {selectedEndpoint.metodo} no requieren cuerpo de envío.</p>
                                  </div>
                                )
                              ) : activeTab === 'params' ? (
                                <div className="space-y-2 h-72 overflow-y-auto pr-2">
                                  {paramsEdit.length === 0 && (
                                    <p className="text-xs text-gray-500 text-center pt-8">Este endpoint no tiene params. Agrega uno abajo.</p>
                                  )}
                                  {paramsEdit.map((p, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        className={inputClass}
                                        value={p.clave}
                                        onChange={(e) => updateParam(i, 'clave', e.target.value)}
                                        placeholder="Nombre del param"
                                      />
                                      <input
                                        type="text"
                                        className={inputClass}
                                        value={p.valor}
                                        onChange={(e) => updateParam(i, 'valor', e.target.value)}
                                        placeholder="Valor"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => removeParam(i)}
                                        className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                                        title="Quitar param"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={addParam}
                                    className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors pt-1"
                                  >
                                    <Plus className="w-3.5 h-3.5" /> Agregar param
                                  </button>
                                </div>
                              ) : activeTab === 'headers' ? (
                                <div className="space-y-2 h-72 overflow-y-auto pr-2">
                                  {headersEdit.length === 0 && (
                                    <p className="text-xs text-gray-500 text-center pt-8">Este endpoint no tiene cabeceras. Agrega una abajo.</p>
                                  )}
                                  {headersEdit.map((h, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        className={inputClass}
                                        value={h.clave}
                                        onChange={(e) => updateHeader(i, 'clave', e.target.value)}
                                        placeholder="Ej: Content-Type"
                                      />
                                      <input
                                        type="text"
                                        className={inputClass}
                                        value={h.valor}
                                        onChange={(e) => updateHeader(i, 'valor', e.target.value)}
                                        placeholder="Ej: application/json"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => removeHeader(i)}
                                        className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                                        title="Quitar cabecera"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={addHeader}
                                    className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors pt-1"
                                  >
                                    <Plus className="w-3.5 h-3.5" /> Agregar cabecera
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-2 h-72 overflow-y-auto pr-2">
                                  <p className="text-[11px] text-gray-500 pb-1">Completa el valor de cada Path Variable detectada en el endpoint.</p>
                                  {nombresPathVariables.map((clave) => {
                                    const falta = pathVariablesFaltantes.includes(clave);
                                    return (
                                      <div key={clave} className="flex items-center gap-2">
                                        <span className={`w-32 flex-shrink-0 font-mono text-xs truncate ${falta ? 'text-red-400' : 'text-gray-400'}`} title={clave}>
                                          :{clave}
                                        </span>
                                        <input
                                          type="text"
                                          className={`${inputClass} ${falta ? 'border-red-500/50' : ''}`}
                                          value={valorPathVariable(clave)}
                                          onChange={(e) => updatePathVariableValor(clave, e.target.value)}
                                          placeholder="Valor"
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>

                          {errorGuardar && (
                            <div className="mt-4 bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{errorGuardar}</div>
                          )}
                          {guardadoOk && (
                            <div className="mt-4 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-lg text-xs">
                              Cambios guardados.
                            </div>
                          )}

                          <div className="mt-6 flex gap-3">
                            <button
                              onClick={handleGuardarCambios}
                              disabled={guardando}
                              className="flex-1 py-4 bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-50 rounded-xl font-bold flex items-center justify-center gap-2.5 transition-all"
                            >
                              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                              {guardando ? 'Guardando...' : 'Guardar cambios'}
                            </button>
                            {enviando ? (
                              <button
                                onClick={handleCancelarEnvio}
                                className="flex-1 py-4 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-xl font-bold flex items-center justify-center gap-2.5 transition-all text-red-300"
                              >
                                <XCircle className="w-4 h-4" /> Cancelar
                              </button>
                            ) : (
                              <button
                                onClick={handleSendRequest}
                                disabled={pathVariablesFaltantes.length > 0}
                                title={pathVariablesFaltantes.length > 0 ? 'Completa todas las path variables antes de enviar' : undefined}
                                className="flex-1 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-blue-600 disabled:hover:to-cyan-600 rounded-xl font-bold flex items-center justify-center gap-2.5 transition-all transform active:scale-[0.98]"
                              >
                                <Play className="w-4 h-4 fill-white" /> Enviar Petición
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </section>

                    <section className="lg:col-span-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[2.5rem] p-8 flex flex-col justify-between">
                      <div className="flex-1 flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold flex items-center gap-2">
                            <FileCode className="w-5 h-5 text-cyan-400" /> Consola de Salida
                          </h3>
                          {resultado && (
                            <button
                              onClick={handleCopyResponse}
                              className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors"
                              title="Copiar respuesta"
                            >
                              {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                            </button>
                          )}
                        </div>

                        {resultado && (
                          <div className="flex items-center gap-3 mb-4 flex-wrap text-xs">
                            <span className={`font-bold px-2 py-1 rounded ${statusBadgeClass(resultado)}`}>
                              {resultado.status || '—'} {resultado.statusText}
                            </span>
                            <span className="text-gray-500">{resultado.durationMs} ms</span>
                            <span className="text-gray-500">{formatBytes(resultado.sizeBytes)}</span>
                          </div>
                        )}

                        {resultado && (
                          <div className="flex border-b border-white/10 mb-3 text-xs font-semibold">
                            <button onClick={() => setOutputTab('body')} className={tabClass(outputTab === 'body')}>Body</button>
                            <button onClick={() => setOutputTab('headers')} className={tabClass(outputTab === 'headers')}>Headers</button>
                          </div>
                        )}

                        {resultado?.error && (resultado.error.message || resultado.error.hint) && (
                          <div className="mb-3 bg-amber-500/10 border border-amber-500/30 text-amber-300 p-2.5 rounded-lg text-[11px] flex items-start gap-1.5">
                            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                            <span>
                              {resultado.error.message}
                              {resultado.error.hint && <> — {resultado.error.hint}</>}
                            </span>
                          </div>
                        )}

                        <div className="flex-1 bg-black/50 border border-white/10 rounded-2xl p-4 font-mono text-[11px] leading-normal overflow-auto min-h-[260px] max-h-[360px] custom-scrollbar text-cyan-400">
                          {enviando ? (
                            <span className="text-gray-500 animate-pulse block">Conectando con SAP Ariba...</span>
                          ) : resultado ? (
                            <pre className="whitespace-pre-wrap">{outputTab === 'body' ? bodyTexto : headersTexto}</pre>
                          ) : (
                            <span className="text-gray-600 italic block">Envía una petición para visualizar la respuesta...</span>
                          )}
                        </div>
                      </div>
                    </section>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function EmptyState({ title, message }: { title: string; message: string }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-[2.5rem] p-12 flex flex-col items-center justify-center text-center gap-3">
      <PackageX className="w-10 h-10 text-gray-600" />
      <h3 className="text-white font-bold">{title}</h3>
      <p className="text-gray-500 text-sm max-w-md">{message}</p>
    </div>
  );
}
